import { Component } from '@mycolorway/tao';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import 'next/stylesheets/components/grid/cells/summary-cell.scss';

const RESULT_TPL = `
<div class="func-name"></div>
<div class="result"></div>
`;

Component('tr-grid-summary-cell', {
  mixins: [eventListenerMixin],

  properties: {
    cellType: String,
    target: String,
    funcName: {
      type: String,
      observer: 'funcNameChanged',
    },
  },

  connected() {
    this.result = this.querySelector('.selected-text');
    this.selector = this.querySelector('.func-selector');

    const tpl = this.querySelector('template');
    if (tpl) {
      this.formattedNum = document.importNode(tpl.content, true)
        .firstElementChild;
    }

    this.listenTo(this.selector, {
      'tao:opened': () => {
        this.classList.add('active');
      },
      'tao:closed': () => {
        this.classList.remove('active');
      },
      'tao:change': (e) => {
        e.stopPropagation();
        this.handleSelectChange(e.detail);
      },
    });
  },

  ready() {
    this.selectFuncName(this.funcName);
  },

  funcNameChanged(name) {
    this.theFunc = this[`${name}Func`];
  },

  handleSelectChange(selected) {
    const tooltip = document.querySelector('.simple-tooltip');
    if (tooltip) tooltip.remove();

    this.selectedText = this.result.textContent;
    if (!selected || selected === 'none') {
      this.clearSelected();
    } else {
      this.funcName = selected;
    }

    this.namespacedTrigger('change', { bubbles: true });
  },

  clearSelected() {
    this.selector.clearSelected();
    this.funcName = '';
    this.render(null);
  },

  isCalculatable() {
    return !!this.theFunc;
  },

  isEmpty(value) {
    switch (this.cellType) {
      case 'multi_select':
      case 'hyperlink':
        return value.length === 0;
      case 'todo_assignee':
        return value === 'no-assignee';
      default:
        return !value;
    }
  },

  selectFuncName(funcName) {
    if (this[`${funcName}Func`]) {
      this.funcName = funcName;
      this.selector.setValue(funcName);
      this.selectedText = this.result.textContent;
    } else {
      this.clearSelected();
    }
  },

  calculate(cells) {
    if (this.isCalculatable()) {
      this.theFunc(cells);
    }
  },

  countAllFunc(cells) {
    this.render(cells.length);
  },

  countEmptyFunc(cells) {
    const value = cells.filter(cell => this.isEmpty(cell.value)).length;
    this.render(value);
  },

  countNotEmptyFunc(cells) {
    const value = cells.filter(cell => !this.isEmpty(cell.value)).length;
    this.render(value);
  },

  countValuesFunc(cells) {
    let sum = 0;
    cells.forEach((cell) => {
      if (this.isEmpty(cell.value)) return;
      switch (this.cellType) {
        case 'multi_select':
        case 'hyperlink':
          sum += cell.value.length;
          break;
        case 'member':
          sum += cell.value.split(',').length;
          break;
        default:
          sum += 1;
      }
    });
    this.render(sum);
  },

  countUniqueValuesFunc(cells) {
    const set = new Set();
    cells.forEach((cell) => {
      if (this.isEmpty(cell.value)) return;
      switch (this.cellType) {
        case 'multi_select':
        case 'hyperlink':
          cell.value.forEach(v => set.add(v));
          break;
        case 'member':
          cell.value.split(',').forEach(v => set.add(v));
          break;
        default:
          set.add(cell.value);
      }
    });
    this.render(set.size);
  },

  percentEmptyFunc(cells) {
    const total = cells.length;
    const empty = cells.filter(cell => this.isEmpty(cell.value)).length;
    this.render(`${Math.round(empty / total * 100)}%`);
  },

  percentNotEmptyFunc(cells) {
    const total = cells.length;
    const empty = cells.filter(cell => !this.isEmpty(cell.value)).length;
    this.render(`${Math.round(empty / total * 100)}%`);
  },

  sumFunc(cells) {
    let total = 0;
    cells.forEach((cell) => {
      if (cell.value) total += parseFloat(cell.value);
    });
    this.renderWithFormat(total);
  },

  averageFunc(cells) {
    let total = 0;
    let count = 0;
    cells.forEach((cell) => {
      if (cell.value) {
        total += parseFloat(cell.value);
        count += 1;
      }
    });
    this.renderWithFormat(Math.round(total / count));
  },

  medianFunc(cells) {
    const values = [];
    cells.forEach((cell) => {
      if (cell.value) values.push(parseFloat(cell.value));
    });
    this.renderWithFormat(median(values));
  },

  minFunc(cells) {
    let min = 0;
    cells.forEach((cell) => {
      if (cell.value) {
        const v = parseFloat(cell.value);
        if (v < min) min = v;
      }
    });
    this.renderWithFormat(min);
  },

  maxFunc(cells) {
    let max = 0;
    cells.forEach((cell) => {
      if (cell.value) {
        const v = parseFloat(cell.value);
        if (v > max) max = v;
      }
    });
    this.renderWithFormat(max);
  },

  rangeFunc(cells) {
    let max = 0;
    let min = Infinity;
    cells.forEach((cell) => {
      if (!cell.value) return;
      const v = parseFloat(cell.value);
      if (v < min) min = v;
      if (v > max) max = v;
    });
    if (min === Infinity) min = 0;
    this.renderWithFormat(max - min);
  },

  percentSelectedFunc(cells) {
    const percent = {};
    let total = 0;

    cells.forEach((cell) => {
      if (!cell.value) return;
      total += 1;

      let item = percent[cell.value];
      if (item) {
        item.dataset.count = parseInt(item.dataset.count, 10) + 1;
      } else {
        item = document.createElement('div');
        item.classList.add('percent');
        item.dataset.value = cell.value;
        item.dataset.count = 1;
        const textEl = cell.getTextContentElement();
        item.dataset.text = textEl.textContent;
        item.dataset.labelColor = textEl.dataset.labelColor;
        item.innerHTML = '&nbsp';
        percent[cell.value] = item;
      }
    });

    if (total === 0) {
      this.render('无');
      return;
    }

    const fragment = document.createElement('div');
    fragment.classList.add('percent-bar');
    const sorted = Object.values(percent)
      .sort((a, b) => a.dataset.count - b.dataset.count);
    sorted.forEach((item) => { /* eslint-disable no-param-reassign */
      const width = Math.round(item.dataset.count / total * 100);
      item.style.width = `${width}%`;
      item.dataset.tooltip = `
        <div class="grid-summary-cell-tip">
          ${width}% ${item.dataset.text}
          <span class="hint">${item.dataset.count}/${total}</span>
        </div>
      `;
      fragment.appendChild(item);
    });

    this.result.title = '';
    this.result.innerHTML = fragment.outerHTML;
  },

  renderWithFormat(value) {
    if (Number.isNaN(value)) {
      this.render('无');
    } else if (this.formattedNum) {
      this.formattedNum.textContent = value;
      this.render(this.formattedNum.outerHTML, value);
    } else {
      this.render(value);
    }
  },

  render(value, raw) {
    if (value === null) {
      this.result.title = '';
      this.result.innerHTML = '';
      return;
    }

    const result = document.createElement('div');
    result.innerHTML = RESULT_TPL;
    result.querySelector('.func-name').textContent = `${this.selectedText}：`;
    result.querySelector('.result').innerHTML = value;
    this.result.title = `${this.selectedText}: ${raw || value}`;
    this.result.innerHTML = result.innerHTML;
  },
});

function median(arr) {
  const mid = Math.floor(arr.length / 2);
  const nums = arr.sort((a, b) => a - b);
  return arr.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
}
